/**
 * Deep Link Hook
 * 
 * Custom hook for handling deep link parameters in the composer.
 * Parses URL query parameters and provides validated deep link data.
 * 
 * @module hooks/useDeepLink
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { parseDeepLinkParams } from '@/utils/landingPageUtils';
import type { DeepLinkParams } from '@/types/landingPage';

/**
 * Hook for parsing and managing deep link parameters
 * 
 * @returns Parsed deep link parameters from URL
 * 
 * @example
 * ```typescript
 * function MyComponent() {
 *   const deepLinkParams = useDeepLink();
 *   
 *   useEffect(() => {
 *     if (deepLinkParams.template) {
 *       console.log('Load template:', deepLinkParams.template);
 *     }
 *   }, [deepLinkParams]);
 * }
 * ```
 */
export function useDeepLink(): DeepLinkParams {
  const [searchParams] = useSearchParams();
  const [params, setParams] = useState<DeepLinkParams>({});

  useEffect(() => {
    // Parse deep link parameters from URL
    const parsedParams = parseDeepLinkParams(searchParams);
    setParams(parsedParams);
  }, [searchParams]);

  return params;
}
